
import {
    ChipOption,
    computed,
    defineComponent,
    Emitter,
    inject,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
    TableData,
    toDisplayString,
    watch
} from 'vue'
import CtrlBetBox from '@/components/ctrl-bet-box-multi.vue'
import { useStore } from '@/store'
import { BetType, EventBuss } from '@/types/global'
import BetStartStopPop from '@/components/bet-start-stop-pop.vue'
import { saveBet } from '@/api'
import audioPlayer, { SOUNDKEY } from '@/utils/sounds'
import { AppMutation } from '@/store/types'
import { useI18n } from 'vue-i18n'

export default defineComponent({
    emits: ['on-update', 'on-betmode-change'],
    components: {
        CtrlBetBox,
        BetStartStopPop
    },
    props: {
        gameType: {
            type: Number as PropType<number>,
            required: false,
            default: -1
        },
        mData: {
            type: Object as PropType<TableData>,
            require: false
        },
        isFreeBaccarat: {
            type: Boolean as PropType<boolean>,
            required: false,
            default: false
        }
    },
    setup(props, { emit }) {
        const { t } = useI18n()
        const store = useStore()
        const emitter = inject('emitter') as Emitter

        const bear8Bet = ref(0)
        const smallTigerBet = ref(0)
        const bigTigerBet = ref(0)
        const dragon7Bet = ref(0)
        const super6Bet = ref(0)

        const tempBear8Bet = ref(0)
        const tempSmallTigerBet = ref(0)
        const tempBigTigerBet = ref(0)
        const tempDragon7Bet = ref(0)
        const tempSuper6Bet = ref(0)

        const pBet = ref<number>(0)
        const bBet = ref<number>(0)
        const ppBet = ref<number>(0)
        const bpBet = ref<number>(0)
        const tBet = ref<number>(0)
        const drBet = ref<number>(0)
        const tgBet = ref<number>(0)
        const tempPBet = ref<number>(0)
        const tempBBet = ref<number>(0)
        const tempPpBet = ref<number>(0)
        const tempBpBet = ref<number>(0)
        const tempTBet = ref<number>(0)
        const tempDrBet = ref<number>(0)
        const tempTgBet = ref<number>(0)
        const showStartAndEndNorify = ref<boolean>(false)
        const timerNotifyStatus = ref<number>(1)

        const bankerBox = ref<any>()
        const playerBox = ref<any>()
        const tieBox = ref<any>()
        const bankerPBox = ref<any>()
        const playerPBox = ref<any>()

        const dragonBox = ref<any>()
        const tigerBox = ref<any>()
        const tieTGBox = ref<any>()

        const bear8Box = ref()
        const smallTigerBox = ref()
        const bigTigerBox = ref()
        const dragon7Box = ref()
        const super6Box = ref()

        const toastMessage = ref<{ id: number | boolean; message: string }[]>(
            []
        )

        onMounted(() => {
            emitter.on(EventBuss.SMALL_TOAST, handleToastMessage)
            emitter.on(EventBuss.GAMESTATUS, handleNotifyStatus)
            removeallToast()

            if (props.mData) {
                resetBetUI()
            }
        })

        onBeforeUnmount(() => {
            emitter.off(EventBuss.SMALL_TOAST, handleToastMessage)
            emitter.off(EventBuss.GAMESTATUS, handleNotifyStatus)
        })

        const handleNotifyStatus = (status: number) => {
            timerNotifyStatus.value = status
            showStartAndEndNorify.value = true

            resetBet()
            resetTempBet()
        }

        const removeallToast = () => {
            toastMessage.value.forEach((toastM) => {
                if (typeof toastM.id === 'number') {
                    clearTimeout(toastM.id)
                    toastM.id = false
                }
            })

            toastMessage.value = []
        }

        const placeBet = (_betType: number) => {
            // check if user can bet
            if (!props.mData) return

            const { mData } = props

            if (userCommandType.value === 1) {
                if (mData.expand.callGunner === 2) {
                    // client betting not allowed if value is 2
                    emitter.emit(EventBuss.SMALL_TOAST, {
                        msg: t('prompts.betting.gunnerbetmode'),
                        id: props.mData?.id || 0
                    })
                    return
                }
            }

            if (isFreeUser.value) {
                emitter.emit(EventBuss.SMALL_TOAST, {
                    msg: t('prompts.betting.gunnerbetmode'),
                    id: props.mData?.id || 0
                })
                return
            }

            // check if its allowed to bet
            if (mData.status !== 1) {
                // countdown timer is done, no betting allowed
                emitter.emit(EventBuss.SMALL_TOAST, {
                    msg: t('prompts.betting.nobetallowed'),
                    id: props.mData?.id || 0
                })
                return
            }

            const _amount = chipValue.value || 0
            if (_amount > 0) {
                switch (_betType) {
                    case BetType.BANKER:
                        tempBBet.value += _amount
                        break
                    case BetType.DRAGON:
                        tempDrBet.value += _amount
                        break
                    case BetType.PLAYER:
                        tempPBet.value += _amount
                        break
                    case BetType.TIGER:
                        tempTgBet.value += _amount
                        break
                    case BetType.BC_TIE:
                    case BetType.DT_TIE:
                        tempTBet.value += _amount
                        break
                    case BetType.PLAYER_PAIR:
                        tempPpBet.value += _amount
                        break
                    case BetType.BANKER_PAIR:
                        tempBpBet.value += _amount
                        break
                    case BetType.BEAR_8:
                        tempBear8Bet.value += _amount
                        break
                    case BetType.SMALL_TIGER:
                        tempSmallTigerBet.value += _amount
                        break
                    case BetType.BIG_TIGER:
                        tempBigTigerBet.value += _amount
                        break
                    case BetType.DRAGON_7:
                        tempDragon7Bet.value += _amount
                        break
                    case BetType.SUPER6:
                        tempSuper6Bet.value += _amount
                        break
                }

                emitter.emit(EventBuss.PLACE_CHIP, {
                    t: _betType,
                    amt: _amount,
                    type: 'temp',
                    deskId: props.mData.id
                })
            }

            callUpdateCallback()
        }

        const callUpdateCallback = () => {
            // const hasConfimedBet =
            //     pBet.value +
            //         bBet.value +
            //         ppBet.value +
            //         bpBet.value +
            //         tBet.value +
            //         drBet.value +
            //         tgBet.value >
            //     0

            const hasTempBet =
                tempPBet.value +
                    tempBBet.value +
                    tempPpBet.value +
                    tempBpBet.value +
                    tempTBet.value +
                    tempDrBet.value +
                    tempTgBet.value +
                    tempSuper6Bet.value +
                    tempBear8Bet.value +
                    tempSmallTigerBet.value +
                    tempBigTigerBet.value +
                    tempDragon7Bet.value >
                0

            emit('on-update', hasTempBet)
        }

        const pushToast = (msg: string) => {
            const _t = {
                id: setTimeout(() => {
                    const _tt = toastMessage.value.shift()
                    if (_tt && typeof _tt.id === 'number') {
                        clearTimeout(_tt.id)
                    }
                }, 1500),
                message: msg
            }
            toastMessage.value.push(_t)
        }

        const handleToastMessage = (message: { msg: string; id: number }) => {
            if (props.mData && props.mData.id === message.id) {
                pushToast(message.msg)
            }
        }

        const handleBetError = (message: string) => {
            pushToast(message)
        }

        const resetBetUI = () => {
            if (props.gameType === 1) {
                // bac
                if (bankerBox.value && bBet.value > 0)
                    bankerBox.value.handleOnPlaceChip({
                        t: BetType.BANKER,
                        amt: bBet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                if (playerBox.value && pBet.value > 0) {
                    playerBox.value.handleOnPlaceChip({
                        t: BetType.PLAYER,
                        amt: pBet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }

                if (tieBox.value && tBet.value > 0) {
                    tieBox.value.handleOnPlaceChip({
                        t: BetType.BC_TIE,
                        amt: tBet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }

                if (bankerPBox.value && bpBet.value > 0) {
                    bankerPBox.value.handleOnPlaceChip({
                        t: BetType.BANKER_PAIR,
                        amt: bpBet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }

                if (playerPBox.value && ppBet.value > 0) {
                    playerPBox.value.handleOnPlaceChip({
                        t: BetType.PLAYER_PAIR,
                        amt: ppBet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }

                if (bear8Box.value && bear8Bet.value > 0) {
                    bear8Box.value.handleOnPlaceChip({
                        t: BetType.BEAR_8,
                        amt: bear8Bet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }

                if (smallTigerBox.value && smallTigerBet.value > 0) {
                    smallTigerBox.value.handleOnPlaceChip({
                        t: BetType.SMALL_TIGER,
                        amt: smallTigerBet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }

                if (bigTigerBox.value && bigTigerBet.value > 0) {
                    bigTigerBox.value.handleOnPlaceChip({
                        t: BetType.BIG_TIGER,
                        amt: bigTigerBet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }

                if (dragon7Box.value && dragon7Bet.value > 0) {
                    dragon7Box.value.handleOnPlaceChip({
                        t: BetType.DRAGON_7,
                        amt: dragon7Bet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }

                if (super6Box.value && super6Bet.value > 0) {
                    super6Box.value.handleOnPlaceChip({
                        t: BetType.SUPER6,
                        amt: super6Bet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }
            } else {
                if (tigerBox.value && tgBet.value > 0) {
                    tigerBox.value.handleOnPlaceChip({
                        t: BetType.TIGER,
                        amt: tgBet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }

                if (dragonBox.value && drBet.value > 0) {
                    dragonBox.value.handleOnPlaceChip({
                        t: BetType.DRAGON,
                        amt: drBet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }

                if (tieTGBox.value && tBet.value > 0) {
                    dragonBox.value.handleOnPlaceChip({
                        t: BetType.DT_TIE,
                        amt: tBet.value,
                        type: 'confirmed',
                        deskId: props.mData?.id || 0
                    })
                }
            }
        }

        const resetBet = () => {
            pBet.value = 0
            bBet.value = 0
            ppBet.value = 0
            bpBet.value = 0
            tBet.value = 0
            drBet.value = 0
            tgBet.value = 0

            bear8Bet.value = 0
            smallTigerBet.value = 0
            bigTigerBet.value = 0
            dragon7Bet.value = 0
            super6Bet.value = 0
        }

        const resetTempBet = () => {
            tempPBet.value = 0
            tempBBet.value = 0
            tempPpBet.value = 0
            tempBpBet.value = 0
            tempTBet.value = 0
            tempDrBet.value = 0
            tempTgBet.value = 0
            tempBear8Bet.value = 0
            tempSmallTigerBet.value = 0
            tempBigTigerBet.value = 0
            tempDragon7Bet.value = 0
            tempSuper6Bet.value = 0

            // bac
            if (bankerBox.value) bankerBox.value.removeTempUIBet()
            if (playerBox.value) playerBox.value.removeTempUIBet()
            if (tieBox.value) tieBox.value.removeTempUIBet()
            if (bankerPBox.value) bankerPBox.value.removeTempUIBet()
            if (playerPBox.value) playerPBox.value.removeTempUIBet()

            // dragon tiger
            if (dragonBox.value) dragonBox.value.removeTempUIBet()
            if (tigerBox.value) tigerBox.value.removeTempUIBet()
            if (tieTGBox.value) tieTGBox.value.removeTempUIBet()

            if (bear8Box.value) bear8Box.value.removeTempUIBet()
            if (smallTigerBox.value) smallTigerBox.value.removeTempUIBet()
            if (bigTigerBox.value) bigTigerBox.value.removeTempUIBet()
            if (dragon7Box.value) dragon7Box.value.removeTempUIBet()
            if (super6Box.value) super6Box.value.removeTempUIBet()

            callUpdateCallback()
        }

        const applyTempBet = () => {
            tempPBet.value = 0
            tempBBet.value = 0
            tempPpBet.value = 0
            tempBpBet.value = 0
            tempTBet.value = 0
            tempDrBet.value = 0
            tempTgBet.value = 0
            tempBear8Bet.value = 0
            tempSmallTigerBet.value = 0
            tempBigTigerBet.value = 0
            tempDragon7Bet.value = 0
            tempSuper6Bet.value = 0

            if (bankerBox.value) bankerBox.value.applyTempUIBet()
            if (playerBox.value) playerBox.value.applyTempUIBet()
            if (tieBox.value) tieBox.value.applyTempUIBet()
            if (bankerPBox.value) bankerPBox.value.applyTempUIBet()
            if (playerPBox.value) playerPBox.value.applyTempUIBet()

            // dragon tiger
            if (dragonBox.value) dragonBox.value.applyTempUIBet()
            if (tigerBox.value) tigerBox.value.applyTempUIBet()
            if (tieTGBox.value) tieTGBox.value.applyTempUIBet()

            if (bear8Box.value) bear8Box.value.applyTempUIBet()
            if (smallTigerBox.value) smallTigerBox.value.applyTempUIBet()
            if (bigTigerBox.value) bigTigerBox.value.applyTempUIBet()
            if (dragon7Box.value) dragon7Box.value.applyTempUIBet()
            if (super6Box.value) super6Box.value.applyTempUIBet()

            callUpdateCallback()
        }

        const confirmTempBet = () => {
            let _params: any = {}
            if (props.gameType === 1) {
                _params = {
                    memberId: memberId.value,
                    tableId: props.mData!.id,
                    shoeNo: props.mData!.expand.shoeNo,
                    juNo: props.mData!.expand.juNo,
                    gameNo: props.mData!.expand.gameNo,
                    he: tempTBet.value,
                    xian: tempPBet.value,
                    zhuangDui: tempBpBet.value,
                    xianDui: tempPpBet.value,
                    superSix: tempSuper6Bet.value,
                    bearEight: tempBear8Bet.value,
                    smallTiger: tempSmallTigerBet.value,
                    bigTiger: tempBigTigerBet.value,
                    dragonSeven: tempDragon7Bet.value,
                    token: token.value
                }

                if (props.isFreeBaccarat) {
                    _params.zhuangFree = tempBBet.value
                } else {
                    _params.zhuang = tempBBet.value
                }
            } else if (props.gameType === 2) {
                _params = {
                    memberId: memberId.value,
                    tableId: props.mData!.id,
                    shoeNo: props.mData!.expand.shoeNo,
                    juNo: props.mData!.expand.juNo,
                    gameNo: props.mData!.expand.gameNo,
                    zhuang: tempDrBet.value,
                    he: tempTBet.value,
                    xian: tempTgBet.value,
                    token: token.value
                }
            }

            store.commit(AppMutation.SHOW_LOADING)

            saveBet(_params)
                .then((response) => {
                    store.commit(AppMutation.HIDE_LOADING)
                    if (response.data.status === 100) {
                        emitter.emit(EventBuss.SMALL_TOAST, {
                            msg: response.data.description,
                            id: props.mData?.id || 0
                        })
                        resetTempBet()
                    } else if (response.data.status === 200) {
                        emitter.emit(EventBuss.SMALL_TOAST, {
                            msg: t('prompts.betting.betsuccess'),
                            id: props.mData?.id || 0
                        })
                        audioPlayer.Play(SOUNDKEY.BET_SUCCESS)
                        applyTempBet()
                    }
                })
                .catch(() => {
                    store.commit(AppMutation.HIDE_LOADING)
                    resetTempBet()
                    emitter.emit(EventBuss.SMALL_TOAST, {
                        msg: t('prompts.networkerror'),
                        id: props.mData?.id || 0
                    })
                })
        }

        const endGame = () => {
            // bac
            if (bankerBox.value) bankerBox.value.resetBetUI()
            if (playerBox.value) playerBox.value.resetBetUI()
            if (tieBox.value) tieBox.value.resetBetUI()
            if (bankerPBox.value) bankerPBox.value.resetBetUI()
            if (playerPBox.value) playerPBox.value.resetBetUI()

            // dragon tiger
            if (dragonBox.value) dragonBox.value.resetBetUI()
            if (tigerBox.value) tigerBox.value.resetBetUI()
            if (tieTGBox.value) tieTGBox.value.resetBetUI()

            if (bear8Box.value) bear8Box.value.resetBetUI()
            if (smallTigerBox.value) smallTigerBox.value.resetBetUI()
            if (bigTigerBox.value) bigTigerBox.value.resetBetUI()
            if (dragon7Box.value) dragon7Box.value.resetBetUI()
            if (super6Box.value) super6Box.value.resetBetUI()

            resetTempBet()
        }

        const bankerTotal = computed((): number => {
            if (props.mData && props.mData.expand.zhuangAmount)
                return props.mData.expand.zhuangAmount

            return 0
        })

        const playerTotal = computed((): number => {
            if (props.mData && props.mData.expand.xianAmount)
                return props.mData.expand.xianAmount
            return 0
        })

        const tieTotal = computed((): number => {
            if (props.mData && props.mData.expand.heAmount)
                return props.mData.expand.heAmount
            return 0
        })

        const playerPairAmount = computed((): number => {
            if (props.mData && props.mData.expand.xianDuiAmount)
                return props.mData.expand.xianDuiAmount
            return 0
        })

        const bankerPairAmount = computed(() => {
            if (props.mData && props.mData.expand.zhuangDuiAmount)
                return props.mData.expand.zhuangDuiAmount
            return 0
        })

        const dragonSevenAmount = computed(() => {
            if (props.mData && props.mData.expand.dragonSevenAmount)
                return props.mData.expand.dragonSevenAmount
            return 0
        })

        const bearEightAmount = computed(() => {
            if (props.mData && props.mData.expand.bearEightAmount)
                return props.mData.expand.bearEightAmount
            return 0
        })

        const bigTigerAmount = computed(() => {
            if (props.mData && props.mData.expand.bigTigerAmount)
                return props.mData.expand.bigTigerAmount
            return 0
        })

        const smallTigerAmount = computed(() => {
            if (props.mData && props.mData.expand.smallTigerAmount)
                return props.mData.expand.smallTigerAmount
            return 0
        })

        const superSixAmount = computed(() => {
            if (props.mData && props.mData.expand.superSixAmount)
                return props.mData.expand.superSixAmount
            return 0
        })

        const chips = computed((): ChipOption => store.getters['chips'])
        const currentChip = computed((): number => store.getters['currentChip'])
        const customChip = computed(() => store.getters['customChip'])
        const memberId = computed(() => store.getters['memberid'])
        const token = computed(() => store.getters['token'])
        const userCommandType = computed(() => store.getters['commandType'])
        const isFreeUser = computed((): boolean => store.getters['isFreeUser'])
        const playMode = computed((): number => store.getters['playMode'])

        const chipValue = computed(() => {
            if (currentChip.value === 22 && customChip.value > 0) {
                return customChip.value
            }

            if (currentChip.value !== -1) {
                return chips.value.value[currentChip.value]
            }

            return 0
        })

        const totalTempBet = computed(() => {
            const _b = [
                tempPBet.value,
                tempBBet.value,
                tempPpBet.value,
                tempBpBet.value,
                tempTBet.value,
                tempDrBet.value,
                tempTgBet.value,
                tempBear8Bet.value,
                tempSmallTigerBet.value,
                tempBigTigerBet.value,
                tempDragon7Bet.value,
                tempSuper6Bet.value
            ].reduce((a, b) => a + b, 0)
            return _b
        })

        const allowBetOnB8andD7 = computed((): boolean => {
            if (!props.mData) return false
            return props.mData.expand.juNo <= 35
        })

        const allowBetOnBigAndSmallTiger = computed((): boolean => {
            if (!props.mData) return false
            return props.mData.expand.juNo <= 35
        })

        const hasLucky6Bet = computed(
            () => super6Bet.value + tempSuper6Bet.value > 0
        )
        const hasBigOrSmallTigerBet = computed(
            () =>
                bigTigerBet.value +
                    tempBigTigerBet.value +
                    smallTigerBet.value +
                    tempSmallTigerBet.value >
                0
        )

        const isSmallTigerGray = computed(
            () => !allowBetOnBigAndSmallTiger.value || hasLucky6Bet.value
        )
        const isBigTigerGray = computed(
            () => !allowBetOnBigAndSmallTiger.value || hasLucky6Bet.value
        )
        const isSuper6Gray = computed(() => hasBigOrSmallTigerBet.value)

        const smallTigerGrayError = computed(() => {
            if (!allowBetOnBigAndSmallTiger.value)
                return t('prompts.smalltigererror')
            if (hasLucky6Bet.value) return t('prompts.super6tigermutual')
            return ''
        })
        const bigTigerGrayError = computed(() => {
            if (!allowBetOnBigAndSmallTiger.value)
                return t('prompts.bigtigererror')
            if (hasLucky6Bet.value) return t('prompts.super6tigermutual')
            return ''
        })
        const super6GrayError = computed(() =>
            hasBigOrSmallTigerBet.value ? t('prompts.super6tigermutual') : ''
        )

        watch(
            () => props.mData,
            (val) => {
                if (val && val.expand.betFormList) {
                    let _pBet = 0
                    let _bBet = 0
                    let _ppBet = 0
                    let _bpBet = 0
                    let _tBet = 0
                    let _drBet = 0
                    let _tgBet = 0
                    let _bear8Bet = 0
                    let _smallTigerBet = 0
                    let _bigTigerBet = 0
                    let _dragon7Bet = 0
                    let _super6Bet = 0
                    let _isFreeBaccarat = props.isFreeBaccarat

                    const { betFormList } = val.expand
                    betFormList.forEach((i) => {
                        if (i.memberId === memberId.value) {
                            const { playName, amount } = i
                            if (playName === '庄') {
                                _bBet += amount
                                _isFreeBaccarat = false
                            } else if (playName === '庄对') {
                                _bpBet += amount
                            } else if (playName === '闲') {
                                _pBet += amount
                            } else if (playName === '闲对') {
                                _ppBet += amount
                            } else if (playName === '和') {
                                _tBet += amount
                            } else if (playName === '龙') {
                                _drBet += amount
                            } else if (playName === '虎') {
                                _tgBet += amount
                            } else if (playName === '超级六') {
                                _super6Bet += amount
                            } else if (playName === '免佣庄') {
                                _bBet += amount
                                _isFreeBaccarat = true
                            } else if (playName === '龙七') {
                                _dragon7Bet += amount
                            } else if (playName === '熊八') {
                                _bear8Bet += amount
                            } else if (playName === '大老虎') {
                                _bigTigerBet += amount
                            } else if (playName === '小老虎') {
                                _smallTigerBet += amount
                            }
                        }
                    })
                    pBet.value = _pBet
                    bBet.value = _bBet
                    ppBet.value = _ppBet
                    bpBet.value = _bpBet
                    tBet.value = _tBet
                    drBet.value = _drBet
                    tgBet.value = _tgBet
                    bear8Bet.value = _bear8Bet
                    smallTigerBet.value = _smallTigerBet
                    bigTigerBet.value = _bigTigerBet
                    dragon7Bet.value = _dragon7Bet
                    super6Bet.value = _super6Bet

                    if (betFormList.length > 0) {
                        emit('on-betmode-change', _isFreeBaccarat)
                    }
                }
            },
            { immediate: true, deep: true }
        )

        return {
            bankerTotal,
            playerTotal,
            tieTotal,
            playerPairAmount,
            bankerPairAmount,
            pBet,
            bBet,
            ppBet,
            bpBet,
            tBet,
            drBet,
            tgBet,
            tempPBet,
            tempBBet,
            tempPpBet,
            tempBpBet,
            tempTBet,
            tempDrBet,
            tempTgBet,
            bear8Bet,
            smallTigerBet,
            bigTigerBet,
            dragon7Bet,
            super6Bet,
            tempBear8Bet,
            tempSmallTigerBet,
            tempBigTigerBet,
            tempDragon7Bet,
            tempSuper6Bet,
            bear8Box,
            smallTigerBox,
            bigTigerBox,
            dragon7Box,
            super6Box,
            allowBetOnB8andD7,
            isSmallTigerGray,
            isBigTigerGray,
            isSuper6Gray,
            smallTigerGrayError,
            bigTigerGrayError,
            super6GrayError,
            dragonSevenAmount,
            bearEightAmount,
            bigTigerAmount,
            smallTigerAmount,
            superSixAmount,
            playMode,
            toastMessage,
            totalTempBet,
            showStartAndEndNorify,
            timerNotifyStatus,
            bankerBox,
            playerBox,
            tieBox,
            bankerPBox,
            playerPBox,
            dragonBox,
            tigerBox,
            tieTGBox,
            endGame,
            resetBetUI,
            confirmTempBet,
            resetTempBet,
            placeBet,
            handleNotifyStatus,
            handleBetError,
            t
        }
    }
})
